import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

import { ROUTES } from 'navigation/constants';
import colors from 'theme/emotion/colors';
import { ONBOARDING_VALIDATION, ONBOARDING_STEPS } from '../../constants';
import { setUserData, setRegistrationStep } from '../../slice';
import { getUserData } from '../../selectors';
import {
  OnboardingBackButton,
  OnboardingActionButton,
  OnboardingButtonSection,
  OnboardingContainer,
  OnboardingScrollContainer,
  OnboardingContentSection,
  OnboardingTitleSection,
  OnboardingTitle,
  OnboardingDescription,
  ViewportHelper,
} from '../shared';

const InputSection = styled('div')({
  width: '100%',
  maxWidth: '28rem',
});

const StyledTextField = styled(TextField)({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.neutral.white,
    borderRadius: '0.75rem',
    fontSize: '1rem',
    fontFamily: 'Poppins, sans-serif',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.25)',
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary.light,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '1rem',
    fontFamily: 'Poppins, sans-serif',
    color: 'rgba(0, 0, 0, 0.6)',
    '&.Mui-focused': {
      color: colors.primary.light,
    },
  },
});

interface NameEntryProps {}

const NameEntry: React.FC<NameEntryProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userData = useSelector(getUserData);
  const [name, setName] = useState(userData.name || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const isValid = name.length >= ONBOARDING_VALIDATION.NAME_MIN_LENGTH && 
                  name.length <= ONBOARDING_VALIDATION.NAME_MAX_LENGTH;

  // Auto-focus input when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle keyboard visibility on mobile
  useEffect(() => {
    const handleFocus = () => {
      // Scroll input into view when keyboard opens
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    };

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener('focus', handleFocus);
      return () => {
        inputElement.removeEventListener('focus', handleFocus);
      };
    }
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length <= ONBOARDING_VALIDATION.NAME_MAX_LENGTH) {
      setName(value);
    }
  };

  const handleContinue = () => {
    if (isValid) {
      dispatch(setUserData({ name: name.trim() }));
      dispatch(setRegistrationStep(ONBOARDING_STEPS.INDUSTRY as any));
      navigate(ROUTES.ONBOARDING.INDUSTRY.path);
    }
  };

  const handleBack = () => {
    navigate(ROUTES.LANDING.path);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isValid) {
      handleContinue();
    }
  };

  return (
    <OnboardingContainer>
      <ViewportHelper />
      <OnboardingBackButton onClick={handleBack} />
      
      <OnboardingScrollContainer>
        <OnboardingContentSection>
          <OnboardingTitleSection>
            <OnboardingTitle>{t('onboarding.greeting')}</OnboardingTitle>
            <OnboardingDescription>
              {t('onboarding.personalizeInfo')}
            </OnboardingDescription>
          </OnboardingTitleSection>

          <InputSection>
            <StyledTextField
              ref={inputRef}
              label={t('onboarding.firstName')}
              value={name}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              variant="outlined"
              autoComplete="given-name"
              inputProps={{
                maxLength: ONBOARDING_VALIDATION.NAME_MAX_LENGTH,
              }}
            />
          </InputSection>
        </OnboardingContentSection>
      </OnboardingScrollContainer>

      <OnboardingButtonSection>
        <OnboardingActionButton
          disabled={!isValid}
          onClick={handleContinue}
        >
          {t('onboarding.continue')}
        </OnboardingActionButton>
      </OnboardingButtonSection>
    </OnboardingContainer>
  );
};

export default NameEntry; 